import React, { useState } from 'react';
import { Box, Grid, Skeleton } from '@mui/material';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';
import { useGetCustomerRiskComplianceHandler } from '../../dataHandlers/getCustomerRiskComplianceHandler';
import { useResolveCustomerDunsHandler } from '../../dataHandlers/resolveCustomerDunsHandler';
import { useLinkCustomerDunsHandler } from '../../dataHandlers/linkCustomerDunsHandler';
import { useUnlinkCustomerDunsHandler } from '../../dataHandlers/unlinkCustomerDunsHandler';
import { useInvestigateCustomerDunsHandler } from '../../dataHandlers/investigateCustomerDunsHandler';
import { useFetchRiskComplianceBlockHandler } from '../../dataHandlers/fetchRiskComplianceBlockHandler';
import CoreCompanyInfoCard from './CoreCompanyInfoCard';
import DnBRiskRatingCard from './DnBRiskRatingCard';
import HierarchiesConnectionsCard from './HierarchiesConnectionsCard';
import OwnershipInsightsCard from './OwnershipInsightsCard';
import DunsRequiredCard from './DunsRequiredCard';
import DunsLinkedStatusBar from './DunsLinkedStatusBar';
import DunsUnlinkConfirmModal from './DunsUnlinkConfirmModal';
import DnbMatchResultsModal from './DnbMatchResultsModal';
import { RISK_CARD_HEIGHT } from './riskComplianceStyles';

function RiskComplianceSkeleton() {
  return (
    <Grid container spacing={2}>
      {[0, 1, 2, 3].map((key) => (
        <Grid key={key} size={{ xs: 12, md: 6 }}>
          <Skeleton variant="rounded" height={RISK_CARD_HEIGHT} />
        </Grid>
      ))}
    </Grid>
  );
}

export default function CustomerRiskComplianceSection({ profileId, isBusinessCustomer, customer }) {
  const { customers: c } = useTranslatedMessages();
  const rc = c?.riskCompliance ?? {};
  const labels = rc?.cards ?? {};
  const [matchCandidates, setMatchCandidates] = useState([]);
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [fetchingBlock, setFetchingBlock] = useState(null);
  const [resolveNoMatch, setResolveNoMatch] = useState(false);

  const { riskComplianceData, isLoading } = useGetCustomerRiskComplianceHandler(profileId, isBusinessCustomer);
  const resolveMutation = useResolveCustomerDunsHandler(profileId);
  const linkMutation = useLinkCustomerDunsHandler(profileId);
  const unlinkMutation = useUnlinkCustomerDunsHandler(profileId);
  const investigateMutation = useInvestigateCustomerDunsHandler(profileId);
  const fetchCompanyInfo = useFetchRiskComplianceBlockHandler(profileId, 'companyInfo');
  const fetchFinancialStrength = useFetchRiskComplianceBlockHandler(profileId, 'financialStrength');
  const fetchHierarchy = useFetchRiskComplianceBlockHandler(profileId, 'hierarchy');
  const fetchOwnership = useFetchRiskComplianceBlockHandler(profileId, 'ownership');

  const blockFetchers = {
    companyInfo: fetchCompanyInfo,
    financialStrength: fetchFinancialStrength,
    hierarchy: fetchHierarchy,
    ownership: fetchOwnership,
  };

  const customerCompanyName = customer?.party_name || customer?.name || '';

  const handleFetchBlock = async (blockKey) => {
    setFetchingBlock(blockKey);
    try {
      await blockFetchers[blockKey].mutateAsync();
    } catch {
      // mutation onError shows snackbar
    } finally {
      setFetchingBlock(null);
    }
  };

  const handleValidateAndLink = async (payload) => {
    try {
      await linkMutation.mutateAsync({
        ...payload,
        companyName: payload.companyName || customerCompanyName,
        successMessage: rc.validateSuccess,
      });
    } catch {
      // mutation onError shows snackbar
    }
  };

  const handleResolve = async () => {
    try {
      const result = await resolveMutation.mutateAsync();
      const candidates = result?.candidates ?? [];

      if (candidates.length === 0) {
        setResolveNoMatch(true);
        setMatchModalOpen(false);
        setMatchCandidates([]);
        return;
      }

      setResolveNoMatch(false);
      setMatchCandidates(candidates);
      setMatchModalOpen(true);
    } catch {
      // mutation onError shows snackbar
    }
  };

  const handleInvestigate = async () => {
    try {
      await investigateMutation.mutateAsync({ successMessage: rc.investigateSuccess });
      setResolveNoMatch(false);
      setMatchModalOpen(false);
      setMatchCandidates([]);
    } catch {
      // mutation onError shows snackbar
    }
  };

  const handleLinkFromMatch = async (payload) => {
    try {
      await linkMutation.mutateAsync({
        ...payload,
        successMessage: rc.linkSuccess,
      });
      setMatchModalOpen(false);
      setMatchCandidates([]);
    } catch {
      // mutation onError shows snackbar
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkMutation.mutateAsync({ successMessage: rc.unlinkSuccess });
      setUnlinkModalOpen(false);
    } catch {
      // mutation onError shows snackbar
    }
  };

  if (!isBusinessCustomer) {
    return (
      <Box sx={{ py: 6, px: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {rc.businessOnlyMessage}
      </Box>
    );
  }

  if (isLoading || !riskComplianceData) {
    return <RiskComplianceSkeleton />;
  }

  const status = riskComplianceData.status;
  const blocks = riskComplianceData.blocks ?? {};
  const emptyMessage = rc.emptyBlockMessage;
  const unavailableMessage = rc.unavailableBlockMessage;
  const noDataMessage = rc.noDataBlockMessage;
  const fetchLabel = rc.fetchBlock;

  if (
    status === 'NOT_STARTED' ||
    status === 'NO_MATCH' ||
    status === 'UNLINKED' ||
    status === 'INVESTIGATION_REQUESTED'
  ) {
    const noMatchState =
      status === 'INVESTIGATION_REQUESTED'
        ? 'investigationRequested'
        : resolveNoMatch || status === 'NO_MATCH'
          ? 'noMatch'
          : null;

    return (
      <>
        <DunsRequiredCard
          customer={customer}
          labels={rc.dunsRequired ?? {}}
          onValidateAndLink={handleValidateAndLink}
          onResolve={handleResolve}
          onInvestigate={handleInvestigate}
          isLinking={linkMutation.isPending}
          isResolving={resolveMutation.isPending}
          isInvestigating={investigateMutation.isPending}
          initialMode={noMatchState ? 'resolve' : 'manual'}
          noMatchState={noMatchState}
        />
        <DnbMatchResultsModal
          open={matchModalOpen}
          onClose={() => setMatchModalOpen(false)}
          candidates={matchCandidates}
          labels={rc.matchModal ?? {}}
          onLink={handleLinkFromMatch}
          onInvestigate={handleInvestigate}
          isLinking={linkMutation.isPending}
        />
      </>
    );
  }

  return (
    <Box>
      <DunsLinkedStatusBar
        dunsNumber={riskComplianceData.dunsNumber}
        companyName={riskComplianceData.companyName}
        linkedLabel={rc.linkedBadge}
        onEdit={() => setUnlinkModalOpen(true)}
      />

      <DunsUnlinkConfirmModal
        open={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
        dunsNumber={riskComplianceData.dunsNumber}
        labels={rc.unlinkModal ?? {}}
        onConfirm={handleUnlink}
        isUnlinking={unlinkMutation.isPending}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CoreCompanyInfoCard
            block={blocks.companyInfo}
            labels={labels.companyInfo ?? {}}
            emptyMessage={emptyMessage}
            unavailableMessage={unavailableMessage}
            noDataMessage={noDataMessage}
            fetchLabel={labels.companyInfo?.fetchLabel ?? fetchLabel}
            onFetch={() => handleFetchBlock('companyInfo')}
            onRefresh={() => handleFetchBlock('companyInfo')}
            isFetching={fetchingBlock === 'companyInfo'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DnBRiskRatingCard
            block={blocks.financialStrength}
            labels={labels.riskRating ?? {}}
            emptyMessage={emptyMessage}
            unavailableMessage={unavailableMessage}
            noDataMessage={noDataMessage}
            fetchLabel={labels.riskRating?.fetchLabel ?? fetchLabel}
            onFetch={() => handleFetchBlock('financialStrength')}
            onRefresh={() => handleFetchBlock('financialStrength')}
            isFetching={fetchingBlock === 'financialStrength'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <HierarchiesConnectionsCard
            block={blocks.hierarchy}
            labels={labels.hierarchy ?? {}}
            emptyMessage={emptyMessage}
            unavailableMessage={unavailableMessage}
            noDataMessage={noDataMessage}
            fetchLabel={labels.hierarchy?.fetchLabel ?? fetchLabel}
            onFetch={() => handleFetchBlock('hierarchy')}
            onRefresh={() => handleFetchBlock('hierarchy')}
            isFetching={fetchingBlock === 'hierarchy'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OwnershipInsightsCard
            block={blocks.ownership}
            labels={labels.ownership ?? {}}
            emptyMessage={emptyMessage}
            unavailableMessage={unavailableMessage}
            noDataMessage={noDataMessage}
            fetchLabel={labels.ownership?.fetchLabel ?? fetchLabel}
            onFetch={() => handleFetchBlock('ownership')}
            onRefresh={() => handleFetchBlock('ownership')}
            isFetching={fetchingBlock === 'ownership'}
          />
        </Grid>
      </Grid>

      <DnbMatchResultsModal
        open={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        candidates={matchCandidates}
        labels={rc.matchModal ?? {}}
        onLink={handleLinkFromMatch}
        isLinking={linkMutation.isPending}
      />
    </Box>
  );
}
